"use client";

import { Overlay } from "@/components/common/overlay";
import { ProjectSectionWrapper } from "@/components/projects/project-section-wrapper";
import { useToggleFavorite } from "@/hooks/use-favorites";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import { MapItemSUP } from "@/types/search";
import { propertyUrlSync } from "@/utils/property-url-sync";
import { useEffect, useRef } from "react";
import { PropertyOverlayContent } from "./index";
interface PropertyOverlayProps {
  onToggleFavorite?: (property: MapItemSUP) => void;
  onShortlist?: (property: MapItemSUP) => void;
  isForPreference?: boolean;
}
export function PropertyOverlay({
  onToggleFavorite,
  onShortlist,
  isForPreference = false
}: PropertyOverlayProps) {
  const isOpen = usePropertyInteractionStore(state => state.isPropertyOverlayOpen);
  const overlaySups = usePropertyInteractionStore(state_0 => state_0.overlaySups);
  const overlayProjectName = usePropertyInteractionStore(state_1 => state_1.overlayProjectName);
  const closePropertyOverlay = usePropertyInteractionStore(state_2 => state_2.closePropertyOverlay);
  const openPropertyOverlay = usePropertyInteractionStore(state_3 => state_3.openPropertyOverlay);
  const overlayProjectId = usePropertyInteractionStore(state_4 => state_4.overlayProjectId);

  // Track if we've already checked URL for propertyId on mount
  const hasCheckedUrlRef = useRef(false);

  // Check URL for propertyId on page load and open overlay if present
  useEffect(() => {
    if (hasCheckedUrlRef.current || isForPreference) return;
    hasCheckedUrlRef.current = true;
    const propertyId = propertyUrlSync.getCurrentPropertyId();
    if (propertyId && !isOpen) {
      // Open the overlay with the propertyId from URL
      // We pass it as sups with just the _id - the carousel will fetch the full data
      openPropertyOverlay({
        sups: [{
          _id: propertyId
        } as MapItemSUP],
        initialSupId: propertyId
      });
    }
  }, [isOpen, isForPreference, openPropertyOverlay]);

  // Track history state to prevent duplicate entries and handle edge cases
  const historyStateRef = useRef<{
    hasHistoryEntry: boolean;
    isProcessingPopState: boolean;
    lastPushTimestamp: number;
  }>({
    hasHistoryEntry: false,
    isProcessingPopState: false,
    lastPushTimestamp: 0
  });

  // Get toggle favorite handler if not provided
  const defaultToggleFavorite = useToggleFavorite(overlaySups && overlaySups[0]);
  const handleToggleFavorite = onToggleFavorite || defaultToggleFavorite;
  const handleClose = () => {
    propertyUrlSync.closeWithoutNavigation();
    closePropertyOverlay();
  };

  // Browser history integration with robust edge case handling
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Prevent race conditions during rapid state changes
      if (historyStateRef.current.isProcessingPopState) {
        return;
      }
      historyStateRef.current.isProcessingPopState = true;
      try {
        const state = event.state as {
          propertyOverlay?: boolean;
        } | null;

        // Edge case 1: User pressed back while overlay is open
        if (isOpen && state?.propertyOverlay === true) {
          // This means we're navigating TO the overlay state, but overlay is already open
          // This can happen if user navigates forward after going back
          // Do nothing - overlay is already in correct state
          return;
        }

        // Edge case 2: User pressed back and overlay should close
        if (isOpen && !state?.propertyOverlay) {
          // Close the overlay when navigating away from it
          closePropertyOverlay();
          historyStateRef.current.hasHistoryEntry = false;
          return;
        }

        // Edge case 3: User pressed back but overlay is already closed
        if (!isOpen && !state?.propertyOverlay) {
          // Do nothing - overlay is already closed, no redirect needed
          return;
        }

        // Edge case 4: Unexpected state - back was pressed but we have propertyOverlay state while overlay is closed
        if (!isOpen && state?.propertyOverlay === true) {
          // This shouldn't happen in normal flow, but handle it gracefully
          // Navigate back one more time to skip this invalid state
          window.history.back();
          return;
        }
      } finally {
        // Reset processing flag after a short delay to handle rapid navigation
        setTimeout(() => {
          historyStateRef.current.isProcessingPopState = false;
        }, 100);
      }
    };

    // When overlay opens, push a new history entry (with deduplication)
    if (isOpen && !historyStateRef.current.hasHistoryEntry) {
      const now = Date.now();
      const timeSinceLastPush = now - historyStateRef.current.lastPushTimestamp;

      // Edge case 5: Prevent duplicate pushState calls within 100ms
      // This handles rapid open/close/open scenarios
      if (timeSinceLastPush < 100) {
        return;
      }

      // Check if current state is already a propertyOverlay state
      // This prevents duplicate entries if overlay was closed and reopened quickly
      const currentState = window.history.state as {
        propertyOverlay?: boolean;
      } | null;
      if (currentState?.propertyOverlay === true) {
        historyStateRef.current.hasHistoryEntry = true;
        return;
      }
      try {
        window.history.pushState({
          propertyOverlay: true
        }, "");
        historyStateRef.current.hasHistoryEntry = true;
        historyStateRef.current.lastPushTimestamp = now;
      } catch (_error) {
        // Edge case 6: Handle pushState quota exceeded error
        // This can happen if too many history entries are created
        // Silently fail - overlay will still work, just without history integration
      }
    }

    // When overlay closes programmatically (not via back button), clean up history
    if (!isOpen && historyStateRef.current.hasHistoryEntry && !historyStateRef.current.isProcessingPopState) {
      // Edge case 7: Overlay was closed programmatically (e.g., via close button)
      // Navigate back to remove the history entry we added
      try {
        historyStateRef.current.hasHistoryEntry = false;
        // Use setTimeout to avoid race condition with state updates
        setTimeout(() => {
          const currentState_0 = window.history.state as {
            propertyOverlay?: boolean;
          } | null;
          if (currentState_0?.propertyOverlay === true) {
            window.history.back();
          }
        }, 0);
      } catch (_error_0) {
        // Silently fail - history cleanup failed but overlay is already closed
      }
    }
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isOpen, closePropertyOverlay]);

  // If isOpen is true and no overlaySups but overlayProjectId exists, render ProjectSectionWrapper
  if (isOpen && !overlaySups?.length && overlayProjectId) {
    return <Overlay isOpen={isOpen} onClose={handleClose}>
				<div className={`@container min-h-screen bg-white max-w-7xl mx-auto mt-4`}>
					<ProjectSectionWrapper projectId={overlayProjectId} />
				</div>
			</Overlay>;
  }

  // If isOpen is false and no overlaySups or overlayProjectId, render null
  if (!isOpen && !overlaySups?.length && !overlayProjectId) {
    return null;
  }

  // If overlaySups exists, render PropertyOverlayContent
  if (overlaySups?.length) {
    return <Overlay isOpen={isOpen} onClose={handleClose}>
				<PropertyOverlayContent sups={overlaySups} projectName={overlayProjectName || ""} onToggleFavorite={handleToggleFavorite} onShortlist={onShortlist} isForPreference={isForPreference} />
			</Overlay>;
  }

  // Default case: render null
  return null;
}